import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { Map } from 'mapbox-gl';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { environment } from '@env/environment';
import { ProjectsService } from 'app/modules/projects/projects.service';
import * as CustomValidator from 'app/shared/directives/custom-validators.directive';
import { Project, Pipeline, ProjectTemplate, Property } from 'app/modules/projects/projects.types';
import { Account, ProjectType, PropertyType, ProjectStage, FieldDef } from 'app/core/models/account.types';
import { AccountService } from 'app/core';
import { Parcel, LatLng } from 'app/modules/research/research.types';
import { ResearchService } from 'app/modules/research/research.service';

@Component({
  selector: 'project-map-add',
  templateUrl: './project-map-add.component.html',
  styleUrls: ['./project-map-add.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectMapAddComponent extends OnDestroyMixin implements OnInit {
  addProjectForm: UntypedFormGroup;
  data: any;
  account: Account;
  project: Project;
  pipeline: Pipeline;
  pipelines: Pipeline[];
  displayColumns: [];
  fieldDefs: FieldDef[] = [];
  options: [];
  projectTypes: ProjectType[];
  propertyTypes: PropertyType[];
  projectStages: ProjectStage[];
  projectTemplates: ProjectTemplate<any>[];
  properties: Property[] = [];
  pdfUrl: string;

  panels: any[];
  private _dashboardDefinition: any;

  @ViewChild('viewer', { static: true }) viewer: ElementRef;
  wvInstance: any;
  public map: Map;
  public parcels: Parcel[];
  public center: LatLng = { latitude: 39.09747, longitude: -94.57989 };
  public parcelTilesVisible: string = 'visible';
  public loadingParcel: boolean = false;

  /**
   * Constructor
   */
  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _researchService: ResearchService,
    private readonly _accountService: AccountService,
    private readonly _projectsService: ProjectsService,
    private readonly _router: Router,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _snackBar: MatSnackBar,
  ) {
    super();
    this._accountService.currentAccount.pipe(untilComponentDestroyed(this)).subscribe((accountData) => {
      this.account = accountData;
    });
  }

  ngOnInit(): void {
    this.wvDocumentLoadedHandler = this.wvDocumentLoadedHandler.bind(this);

    this.addProjectForm = this._formBuilder.group({
      title: ['', [Validators.required, CustomValidator.noWhiteValidator]],
      property_type_id: ['', [Validators.required]],
      project_type_id: [''],
      pipeline_id: ['', [Validators.required]],
      template_id: ['', [Validators.required]],
      occupancy: [''],
      broker_guidance_price: [''],
      going_in_noi: [''],
      unit_size: [''],
      description: [''],
      document_id: [''],
      property: this._formBuilder.group({
        property_type_id: [''],
        apn: [''],
        remote_id: [''],
        address: this._formBuilder.group({
          address_string: '',
          street: '',
          state: '',
          postal_code: '',
          latitude: '',
          longitude: '',
        }),
      }),
    });

    this._activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.data = data;
      this.fieldDefs = data.fieldDefs;
      this.pipeline = data.pipeline;
      this.options = data.options;
      this.pipelines = this.data.options.pipelines.filter((pipeline) => pipeline?.pipeline_type != 'dynamic');
      this.projectTypes = this.data.options.project_types.filter((option) => !option.hidden);
      this.propertyTypes = this.data.options.property_types.filter((option) => !option.hidden);
      this.projectStages = this.data.options.property_types.filter((option) => !option.hidden);
      this.projectTemplates = this.data.options.project_templates
        .filter((option) => !option.hidden && option.related_to_type == 'Project')
        .sort((a, b) => a.name.localeCompare(b.name));

      this.addProjectForm.get('pipeline_id').patchValue(this.pipeline.id);
    });
  }

  wvDocumentLoadedHandler(): void {
    const annotManager = this.wvInstance.annotManager;
    // and access classes defined in the WebViewer iframe
    const { Annotations } = this.wvInstance;
    const rectangle = new Annotations.RectangleAnnotation();
    rectangle.PageNumber = 1;
    rectangle.X = 100;
    rectangle.Y = 100;
    rectangle.Width = 250;
    rectangle.Height = 250;
    rectangle.StrokeThickness = 5;
    rectangle.Author = annotManager.getCurrentUser();
    // see https://www.pdftron.com/api/web/WebViewer.html for the full list of low-level APIs
  }

  /**
   * Discard the message
   */
  discard(): void {
    this._router.navigate(['../'], { relativeTo: this._activatedRoute });
  }

  onSelectParcel(event: any): void {
    this.loadingParcel = true;
    const feature = event.features[0];

    this._researchService
      .reverseGeocode(feature.properties.id, event.lngLat.lat, event.lngLat.lng)
      .pipe(untilComponentDestroyed(this))
      .subscribe((data) => {
        if (data && data.address) {
          this.properties.push({
            id: feature.properties.id,
            apn: feature.properties.apn,
            address: {
              address_string: data.address,
              latitude: event.lngLat.lat,
              longitude: event.lngLat.lng,
            },
          } as unknown as Property);
          this._changeDetectorRef.markForCheck();
        }
        this.loadingParcel = false;
      });
  }

  onSelectLatLng(event: any): void {
    this.loadingParcel = true;

    this._researchService
      .reverseGeocode(null, event.lngLat.lat, event.lngLat.lng)
      .pipe(untilComponentDestroyed(this))
      .subscribe((data) => {
        if (data && data.address) {
          this.properties.push({
            id: null,
            apn: null,
            address: {
              address_string: data.address,
              latitude: event.lngLat.lat,
              longitude: event.lngLat.lng,
            },
          } as unknown as Property);
          this._changeDetectorRef.markForCheck();
        }
        this.loadingParcel = false;
      });
  }

  onDeSelectParcel($event): void {
    let feature = $event.features[0];
    let index = this.properties.findIndex((option) => option.id === feature.properties.id);
    if (index >= 0) {
      this.properties.splice(index, 1);
      this._changeDetectorRef.markForCheck();
    }
  }

  save(): void {
    this.addProjectForm.disable();

    let project = this.addProjectForm.value;
    this._projectsService
      .saveProject(project)
      .pipe(untilComponentDestroyed(this))
      .subscribe((project) => {
        for (let prop of this.properties) {
          if (prop.address.address_string) {
            let property = this.addProjectForm.get('property').value;
            property.property_type_id = this.addProjectForm.get('property_type_id').value;
            property.address = prop.address;

            this._projectsService
              .addProperty(property)
              .pipe(untilComponentDestroyed(this))
              .subscribe((property) => {});
          }
        }
        this._router.navigate(['/projects', project.pipeline_id, project.id], { relativeTo: this._activatedRoute });
      });
  }

  saveAndCreateNew(): void {
    this.addProjectForm.disable();

    let project = this.addProjectForm.value;
    this._projectsService
      .saveProject(project)
      .pipe(untilComponentDestroyed(this))
      .subscribe((project) => {
        for (let prop of this.properties) {
          if (prop.address.address_string) {
            let property = this.addProjectForm.get('property').value;
            property.property_type_id = this.addProjectForm.get('property_type_id').value;
            property.address = prop.address;

            this._projectsService
              .addProperty(property)
              .pipe(untilComponentDestroyed(this))
              .subscribe((property) => {});
          }
        }
        this._router.routeReuseStrategy.shouldReuseRoute = () => false;
        this._router.onSameUrlNavigation = 'reload';
        this._router.navigate(['/projects', 'map']);
        return this._snackBar.open('Deal created successfully.', '', {
          duration: 3000,
        });
      });
  }

  /**
   * Passed through to panels to be called when a field has been updated
   */
  update(params) {
    const options = { ...params, id: this.project?.id };
    this._projectsService
      .saveProject(options)
      .pipe(untilComponentDestroyed(this))
      .subscribe((project) => {});
  }

  lookupField(name) {
    let fields = this.fieldDefs.filter((fieldDef) => fieldDef.name == name);
    if (fields.length > 0) {
      return fields[0];
    } else {
      return null;
    }
  }

  /**
   * Called from the individual panels when modifying the panel definition
   */
  onEditPanelDef($event): void {
    // Let's find the panel with this id and replace it
    for (let col in this.panels) {
      for (let row in this.panels[col]) {
        let panel = this.panels[col][row];
        if (panel.id == $event.id) {
          this.panels[col][row] = $event;
        }
      }
    }
    this._dashboardDefinition.panels = this.panels;
    this.pipeline.dashboard_definition = JSON.stringify(this._dashboardDefinition);
    this._projectsService.updatePipeline(this.pipeline).pipe(untilComponentDestroyed(this)).subscribe();
  }

  /**
   * Track by function for ngFor loops
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.address_string || index;
  }
}
